import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserContextResponse } from './generated/sportSchool';

interface School {
  id: string;
  name: string;
}

interface AuthState {
  token: string | null;
  school: School | null;
  userContext: UserContextResponse | null;
  activeRole: string | null;
  impersonationToken: string | null;
  impersonationRefreshToken: string | null;
  originalToken: string | null;
  originalUserContext: UserContextResponse | null;
  originalActiveRole: string | null;
  originalPath: string | null;
  setToken: (token: string | null) => void;
  setSchool: (school: School | null) => void;
  setUserContext: (userContext: UserContextResponse | null) => void;
  setActiveRole: (activeRole: string | null) => void;
  startImpersonation: (
    impersonationToken: string,
    impersonationRefreshToken: string,
    originalToken: string,
    originalUserContext: UserContextResponse,
    originalActiveRole: string | null,
    originalPath?: string | null
  ) => void;
  stopImpersonation: () => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      school: null,
      userContext: null,
      activeRole: null,
      impersonationToken: null,
      impersonationRefreshToken: null,
      originalToken: null,
      originalUserContext: null,
      originalActiveRole: null,
      originalPath: null,
      setToken: (token) => set({ token }),
      setSchool: (school) => set({ school }),
      setUserContext: (userContext) => set({ userContext }),
      setActiveRole: (activeRole) => set({ activeRole }),
      startImpersonation: (impersonationToken, impersonationRefreshToken, originalToken, originalUserContext, originalActiveRole, originalPath) =>
        set({
          impersonationToken,
          impersonationRefreshToken,
          originalToken,
          originalUserContext,
          originalActiveRole,
          originalPath: originalPath ?? null,
        }),
      stopImpersonation: () =>
        set((state) => ({
          userContext: state.originalUserContext,
          activeRole: state.originalActiveRole ?? 'Admin',
          impersonationToken: null,
          impersonationRefreshToken: null,
          originalToken: null,
          originalUserContext: null,
          originalActiveRole: null,
          originalPath: null,
        })),
      logout: () => set({
        token: null,
        school: null,
        userContext: null,
        activeRole: null,
        impersonationToken: null,
        impersonationRefreshToken: null,
        originalToken: null,
        originalUserContext: null,
        originalActiveRole: null,
        originalPath: null,
      }),
    }),
    {
      name: 'ca-auth',
      partialize: (state) => ({
        token: state.token,
        school: state.school,
        userContext: state.userContext,
        activeRole: state.activeRole,
        impersonationToken: state.impersonationToken,
        impersonationRefreshToken: state.impersonationRefreshToken,
        originalToken: state.originalToken,
        originalUserContext: state.originalUserContext,
        originalActiveRole: state.originalActiveRole,
        originalPath: state.originalPath,
      }),
    }
  )
);

interface SearchState {
  query: string;
  setQuery: (query: string) => void;
}

export const useSearchStore = create<SearchState>((set) => ({
  query: '',
  setQuery: (query) => set({ query }),
}));
